import { Injectable } from '@angular/core';
import { QuestionnaireRequest } from 'src/API';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { MedicalHistoryPopulatorKey, MedicalHistorySource } from '../medical-history-source.model';

/**
 * Maps a questionnaire `type` to the populator that knows how to apply it.
 * Adding support for a new questionnaire type is a one-line addition here (OCP)
 * rather than a new branch in the component. DERFS/CDERFS are handled
 * separately because only the most recently completed one is applied.
 */
const POPULATOR_BY_QUESTIONNAIRE_TYPE: {
  [questionnaireType: string]: MedicalHistoryPopulatorKey;
} = {
  CUSTOM: 'custom',
  FITZPATRICK: 'custom',
  MEDICATION: 'custom',
  SPEED: 'speed',
  DEWSIII_Q: 'dewsIII'
};
/**
 * Turns raw questionnaire requests into the ordered list of sources the
 * linking service applies. Owns the "latest completed DERFS wins" rule.
 */
@Injectable({ providedIn: 'root' })
export class QuestionnaireSourceClassifier {
  classifyLinked(questionnaires: QuestionnaireRequest[]): MedicalHistorySource[] {
    if (!questionnaires) {
      return [];
    }

    const classifiable: Array<{
      questionnaire: QuestionnaireRequest;
      populatorKey: MedicalHistoryPopulatorKey;
    }> = [];
    let latestDerfs: QuestionnaireRequest = null;

    questionnaires.forEach(questionnaire => {
      if (this.isDerfs(questionnaire)) {
        latestDerfs = this.pickLatestCompleted(latestDerfs, questionnaire);
        return;
      }
      const populatorKey = POPULATOR_BY_QUESTIONNAIRE_TYPE[questionnaire.type];
      if (populatorKey) {
        classifiable.push({ questionnaire, populatorKey });
      }
    });

    // Only the single latest DERFS/CDERFS is applied, but it is positioned by
    // its own completion date rather than forced last.
    if (latestDerfs) {
      classifiable.push({ questionnaire: latestDerfs, populatorKey: 'derfs' });
    }

    // Earliest completed first, so the most recently completed questionnaire is
    // applied last and its data wins on any overlapping control.
    classifiable.sort(
      (a, b) => this.dateOrZero(a.questionnaire) - this.dateOrZero(b.questionnaire)
    );

    return classifiable.map(entry =>
      entry.populatorKey === 'derfs'
        ? { populatorKey: entry.populatorKey, data: entry.questionnaire }
        : {
            populatorKey: entry.populatorKey,
            data: safeJsonParse(entry.questionnaire.response['answers'])
          }
    );
  }

  getLatestCompletedDerfs(questionnaires: QuestionnaireRequest[]): QuestionnaireRequest {
    if (!questionnaires) {
      return null;
    }
    return questionnaires
      .filter(questionnaire => this.isDerfs(questionnaire) && questionnaire.completedAt)
      .reduce(
        (latest, candidate) => this.pickLatestCompleted(latest, candidate),
        null as QuestionnaireRequest
      );
  }

  private isDerfs(questionnaire: QuestionnaireRequest): boolean {
    return questionnaire.type === 'DERFS' || questionnaire.type === 'CDERFS';
  }

  private dateOrZero(questionnaire: QuestionnaireRequest): number {
    return questionnaire.completedAt ? new Date(questionnaire.completedAt).getTime() : 0;
  }

  private pickLatestCompleted(
    current: QuestionnaireRequest,
    candidate: QuestionnaireRequest
  ): QuestionnaireRequest {
    if (!current) {
      return candidate;
    }
    return new Date(current.completedAt).getTime() < new Date(candidate.completedAt).getTime()
      ? candidate
      : current;
  }
}
